import { Calendar, ConfigProvider, Modal, notification, Skeleton, Spin } from 'antd';
import enUS from 'antd/locale/en_US';
import etEE from 'antd/locale/et_EE';
import ruRU from 'antd/locale/ru_RU';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/et';
import 'dayjs/locale/ru';
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DATE_FORMAT } from '@/constants';
import { useGetAdditionalDischarges } from '@/features/objectItems/api/use-get-additional-discharges';
import { useGetObjectItemDischarges } from '@/features/objectItems/api/use-get-object-item-discharges';
import { useOrderAdditionalDischarge } from '@/features/objectItems/api/use-order-additional-discharge';

import { ChevronLeft, ChevronRight, Map01, Plus, X } from '@untitled-ui/icons-react';

import { formatAsCurrency } from '@/helpers/number-formatter';

import { useQueryClient } from '@tanstack/react-query';

import './WasteCalendar.css';

import { useCancelDischarge } from '@/features/objectItems/api/use-cancel-discharge';
import { cn } from '@/utils/cn';
import Select from '@/components/ui/Select';
import { useGetFilters } from '@/features/desktop/api/use-get-filters';
import { useUser } from '@/features/auth';
import { useUserInfoStore } from '@/store';
import RadioButton from '@/components/ui/RadioButton';
import Checkbox from '@/components/ui/Checkbox';
import { useGetObjectItems } from '@/features/objectItems/api/use-get-object-items';

import { CustomerContractRole } from '@ekt-group/general-purpose-api-interfaces/src/types/customer-contract-role.enum';

import { getAllowedContractIdsFromPermissions } from '@/helpers/get-allowed-contract-ids-from-permissions';

import { ObjectItemSideloading } from '@ekt-group/general-purpose-api-interfaces/src/types/object-items.enum';

import { useGetObjects } from '@/features/objects/api/use-get-objects';

import { ObjectsSideloading } from '@ekt-group/general-purpose-api-interfaces/src/modules/objects/types/objects-sideloading.enum';
import { ObjectEntity } from '@ekt-group/general-purpose-api-interfaces';

import { useBreakpoint } from '@/hooks/use-breakpoints';

dayjs.extend(utc);
dayjs.extend(updateLocale);
dayjs.updateLocale('en', { weekStart: 1 });

type Props = {
  objectItemId?: number;
  garbageId?: number;
  objectAddressId?: number;
  isModalOpen: boolean;
  handleCancel: () => void;
};

const ObjectItemDischargesModal = ({ isModalOpen, handleCancel, objectItemId, garbageId, objectAddressId }: Props) => {
  const { t, i18n } = useTranslation('wasteCalendar');
  const [currentMonth, setCurrentMonth] = useState<Date | string>(new Date());
  const [selectedDate, setSelectedDate] = useState<string>();

  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
  const [selectedGarbageId, setSelectedGarbageId] = useState<number>(garbageId);
  const [selectedObjectItemIds, setSelectedObjectItemIds] = useState<number[]>([objectItemId]);
  const [selectedObjectAddressId, setSelectedObjectAddressId] = useState<number>(objectAddressId);

  const { isBelowMd } = useBreakpoint('md');

  const queryClient = useQueryClient();

  const { data: currentUser } = useUser();
  const { selectedAccount } = useUserInfoStore();

  const todayDate = useMemo(() => new Date().toISOString(), []);
  const currentLanguage = i18n.language as 'et' | 'en' | 'ru';

  const localeMap = {
    en: { dayjs: 'en', antd: enUS },
    ru: { dayjs: 'ru', antd: ruRU },
    et: { dayjs: 'et', antd: etEE },
  };

  const filtersQuery = useGetFilters({
    userId: currentUser.id,
    types: ['addresses', 'garbage', 'itemType'],
    ownerCodes: [selectedAccount?.code],
  });

  const objectsQuery = useGetObjects({
    params: {
      contractIds:
        currentUser?.roles?.includes(CustomerContractRole.ChildContract) ||
        currentUser?.roles?.includes(CustomerContractRole.SharedObjectContract)
          ? getAllowedContractIdsFromPermissions(selectedAccount?.permissions)
          : undefined,
      sideloading: [ObjectsSideloading.Contract, ObjectsSideloading.ObjectAddress, ObjectsSideloading.IsActive],
      ownerCodes: [selectedAccount?.code],
      isActive: true,
      isDeleted: false,
      disablePagination: true,
    },
    queryConfig: {
      enabled: Boolean(selectedAccount?.code && isModalOpen),
    },
  });
  const objectsData: ObjectEntity[] = objectsQuery.data as ObjectEntity[];
  const objectId = objectsData?.find((object) => object.objectAddress?.id === selectedObjectAddressId)?.id;

  useEffect(() => {
    const object = objectsData?.find((object) => object.id === objectId);
    if (isModalOpen && object && object.contract?.contractDebt) {
      notification.error({ message: t('contractHasDebt') });
    }
  }, [objectId, isModalOpen]);

  const objectItemsQuery = useGetObjectItems({
    params: {
      contractIds:
        currentUser?.roles?.includes(CustomerContractRole.ChildContract) ||
        currentUser?.roles?.includes(CustomerContractRole.SharedObjectContract)
          ? getAllowedContractIdsFromPermissions(selectedAccount?.permissions)
          : undefined,
      ownerCodes: [selectedAccount?.code],
      objectIds: objectsData?.map((object) => object.id),
      disablePagination: true,
      sideloading: [ObjectItemSideloading.Garbage, ObjectItemSideloading.ItemType, ObjectItemSideloading.IsActive],
      isActive: true,
      isDeleted: false,
    },
    queryConfig: {
      enabled: Boolean(objectsData?.length && selectedAccount?.code && isModalOpen),
    },
  });

  const isSelectedObjectItemIdsBelongToObject =
    selectedObjectItemIds?.length > 0 &&
    selectedObjectItemIds?.every((objectItemId) => {
      const objectItem = objectItemsQuery.data?.find((objectItem) => objectItem.id === objectItemId);
      return objectItem?.objectId === objectId;
    });

  const objectItemDischargesQuery = useGetObjectItemDischarges({
    params: {
      objectItemIds: selectedObjectItemIds,
      objectIds: [objectId],
      garbageIds: [selectedGarbageId],
      disablePagination: true,
      isActive: true,
      startDate: todayDate,
      isDeleted: false,
    },
    queryConfig: {
      enabled: Boolean(selectedGarbageId && objectId && isSelectedObjectItemIdsBelongToObject && isModalOpen),
    },
  });

  const availableObjectItems = useMemo(() => {
    return objectItemsQuery.data?.filter((objectItem) => objectItem.garbage?.id === selectedGarbageId && objectItem.objectId === objectId);
  }, [objectItemsQuery.data, selectedGarbageId, objectId]);

  const availableGarbageIds =
    objectItemsQuery.data?.filter((objectItem) => objectItem.objectId === objectId)?.map((objectItem) => objectItem.garbageId) || [];

  const availableGarbage = useMemo(() => {
    return filtersQuery.data?.garbage?.filter((garbage) => availableGarbageIds.includes(garbage.id));
  }, [objectItemsQuery.data, availableGarbageIds]);

  const additionalDischargesQuery = useGetAdditionalDischarges({
    params: {
      garbageId: selectedGarbageId,
      objectId,
    },
    queryConfig: {
      enabled: Boolean(selectedGarbageId && objectId && isModalOpen),
    },
  });

  const orderAdditionalDischargeMutation = useOrderAdditionalDischarge();
  const cancelDischargeMutation = useCancelDischarge();

  const mergedAdditionalDischargeDates: Record<string, number> = {};

  for (const group of additionalDischargesQuery.data || []) {
    if (!selectedObjectItemIds.includes(group.objectItemId)) {
      continue;
    }

    for (const discharge of group.additionalDischarges) {
      const { date, successPriceWithVat } = discharge;
      const price = parseFloat(successPriceWithVat);
      if (!mergedAdditionalDischargeDates[date]) {
        mergedAdditionalDischargeDates[date] = 0;
      }
      mergedAdditionalDischargeDates[date] += price;
    }
  }

  const additionalDischargeDates = Object.entries(mergedAdditionalDischargeDates).map(([date, price]) => ({
    date: dayjs(date).format(DATE_FORMAT),
    price,
  }));

  const groupedDischargeDates: Record<
    string,
    {
      pricesByObjectItem: Record<number, number>;
      isExtraDischarge: boolean;
    }
  > = {};

  for (const entry of objectItemDischargesQuery.data || []) {
    const date = entry.date;
    const objectItemId = entry.objectItem.id;
    const price = parseFloat(entry.priceWithVat);
    const isExtraDischarge = entry.isExtraDischarge;

    if (!groupedDischargeDates[date]) {
      groupedDischargeDates[date] = {
        pricesByObjectItem: {},
        isExtraDischarge: false,
      };
    }

    if (!groupedDischargeDates[date].pricesByObjectItem[objectItemId]) {
      groupedDischargeDates[date].pricesByObjectItem[objectItemId] = 0;
    }

    groupedDischargeDates[date].pricesByObjectItem[objectItemId] += price;

    if (isExtraDischarge) {
      groupedDischargeDates[date].isExtraDischarge = true;
    }
  }

  const dischargeDatesAndPrices = Object.entries(groupedDischargeDates).map(([date, data]) => {
    const totalPrice = Object.values(data.pricesByObjectItem).reduce((sum, p) => sum + p, 0);
    return {
      date: dayjs(date).format(DATE_FORMAT),
      priceWithVat: totalPrice.toFixed(2),
      isExtraDischarge: data.isExtraDischarge,
    };
  });

  const dischargeDates = dischargeDatesAndPrices.map((discharge) => discharge.date);

  const handleMonthChange = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setCurrentMonth(dayjs(currentMonth).subtract(1, 'month').toDate());
    } else {
      setCurrentMonth(dayjs(currentMonth).add(1, 'month').toDate());
    }
  };

  const isDischargeDate = selectedDate && dischargeDates.includes(dayjs(selectedDate).format(DATE_FORMAT));

  const getPriceByDate = (date: string) => {
    let price;

    if (isDischargeDate) {
      price = dischargeDatesAndPrices?.find((discharge) => discharge.date === dayjs(date).format(DATE_FORMAT))?.priceWithVat;
    } else {
      price = additionalDischargeDates?.find(
        (additionalDischargeDate) => additionalDischargeDate.date === dayjs(date).format(DATE_FORMAT),
      )?.price;
    }

    return formatAsCurrency(Number(price) || 0);
  };

  const handleDateSelect = (date: Dayjs) => {
    setSelectedDate(dayjs(date).format('YYYY-MM-DD'));
    setIsDetailsModalVisible(true);
  };

  const renderDate = (date: Dayjs) => {
    const formattedDate = dayjs(date).format(DATE_FORMAT);
    const additionalDischargeDate = additionalDischargeDates.find(({ date }) => date === formattedDate);
    const isDischargeDate = dischargeDates.includes(formattedDate);

    const discharge = dischargeDatesAndPrices?.find((discharge) => discharge.date === dayjs(date).format(DATE_FORMAT));

    let dateElement = (
      <div className="h-full w-full flex items-center justify-center">
        <span className="text-[#B9B9B9] sm:text-base text-[10px]">{dayjs(date).format('D')}</span>
      </div>
    );

    if (additionalDischargeDate) {
      dateElement = (
        <div className="h-full text-gray-10 font-medium w-full sm:px-[8px] flex flex-col items-center justify-between sm:pb-2 pt-2 bg-[#FB923C] rounded-[8px]">
          <span className="sm:text-base text-[12px]">{dayjs(date).format('D')}</span>
          <div className="bg-[#FFB273] h-[34px] flex items-center justify-center rounded-[4px] w-full text-center">
            <span className="overflow-hidden text-ellipsis whitespace-nowrap sm:text-[18px] text-[10px]">
              {formatAsCurrency(Number(additionalDischargeDate?.price) || 0)}
            </span>
          </div>
        </div>
      );
    }

    if (isDischargeDate) {
      dateElement = (
        <div className="h-full text-gray-10 font-medium w-full sm:px-[8px] flex flex-col items-center sm:pb-2 pt-2 justify-between bg-[#EBF163] rounded-[8px]">
          <div className="flex w-full justify-between">
            <span className="sm:h-5 sm:w-5 w-4 h-4" />
            <span className="text-gray-10 sm:text-base text-[12px] font-medium">{dayjs(date).format('D')}</span>
            {discharge?.isExtraDischarge ? (
              <Plus className="sm:h-5 sm:w-5 w-4 h-4 text-[#A7AD2A]" />
            ) : (
              <span className="sm:w-5 sm:h-5 w-4 h-4" />
            )}
          </div>
          <span className="bg-[#F3F7A8] sm:text-[18px] text-[10px] h-[34px] flex items-center justify-center rounded-[4px] w-full text-center">
            {formatAsCurrency(Number(discharge?.priceWithVat) || 0)}
          </span>
        </div>
      );
    }

    return (
      <div
        title={dayjs(date).format(DATE_FORMAT)}
        className="w-full font-medium border border-[#EFEFEF] sm:h-[93px] h-[63px] px-[3px] py-[4px]"
      >
        {dateElement}
      </div>
    );
  };

  const handleOrderAdditionalDischarge = () => {
    orderAdditionalDischargeMutation.mutate(
      selectedObjectItemIds.map((selectedObjectItemId) => ({
        objectItemId: selectedObjectItemId,
        date: selectedDate,
      })),
      {
        onSuccess: async () => {
          setIsDetailsModalVisible(false);
          notification.success({
            message: t('additionalDischargeOrdered'),
          });

          await queryClient.invalidateQueries({
            predicate: (query) => ['object-item-discharges'].includes((query.queryKey as string[])[0]),
          });
        },
        onError: async () => {
          setIsDetailsModalVisible(false);
          notification.error({
            message: t('somethingWentWrong', { ns: 'common' }),
          });
        },
      },
    );
  };

  const handleCancelDischarge = async () => {
    const discharges = objectItemDischargesQuery.data?.filter((discharge) => discharge.date === selectedDate);

    const promises = discharges.map((discharge) =>
      cancelDischargeMutation.mutate(discharge.id, {
        onSuccess: async () => {
          setIsDetailsModalVisible(false);
          notification.success({
            message: t('dischargeCancelled'),
          });

          await queryClient.invalidateQueries({
            predicate: (query) => ['object-item-discharges', 'additional-discharges'].includes((query.queryKey as string[])[0]),
          });
        },
        onError: async () => {
          setIsDetailsModalVisible(false);
          notification.error({
            message: t('somethingWentWrong', { ns: 'common' }),
          });
        },
      }),
    );
    await Promise.all(promises);
  };

  const disabledDate = (current: Dayjs) => {
    const formattedDate = current.format(DATE_FORMAT);
    const isDischargeDate = dischargeDates.includes(formattedDate);
    const additionalDischargeDate = additionalDischargeDates?.find(({ date }) => date === formattedDate);

    const discharge = objectItemDischargesQuery.data?.find((discharge) => discharge.date === current.format('YYYY-MM-DD'));

    return !((isDischargeDate && discharge.isExtraDischarge) || additionalDischargeDate);
  };

  const handleObjectItemSelect = (objectItemId: number) => {
    setSelectedObjectItemIds((prev) => (prev.includes(objectItemId) ? prev.filter((id) => id !== objectItemId) : [...prev, objectItemId]));
  };

  useEffect(() => {
    setSelectedGarbageId(garbageId);
  }, [garbageId]);

  useEffect(() => {
    const defaultObjectAddress = filtersQuery.data?.addresses?.[0];
    if (defaultObjectAddress) {
      setSelectedObjectAddressId(objectAddressId || defaultObjectAddress?.id);
    }
    const objectItem = objectItemsQuery.data?.find((objectItem) => objectItem.id === objectItemId);
    setSelectedGarbageId(objectItem?.garbageId);

    setSelectedObjectItemIds([objectItemId]);
  }, [filtersQuery.data, objectItemsQuery.data, objectItemId, objectAddressId]);

  useEffect(() => {
    if (availableGarbage?.[0] || filtersQuery.data?.garbage?.[0]?.id) {
      setSelectedGarbageId(availableGarbage?.[0]?.id || filtersQuery.data?.garbage?.[0]?.id);
    }
  }, [filtersQuery.data, objectItemsQuery.data, selectedObjectAddressId]);

  useEffect(() => {
    setSelectedObjectItemIds(availableObjectItems?.map((objectItem) => objectItem.id) || []);
  }, [availableObjectItems]);

  const handleSelectAllObjectItems = () => {
    if (availableObjectItems?.length === selectedObjectItemIds.length) {
      return setSelectedObjectItemIds([]);
    }

    setSelectedObjectItemIds(availableObjectItems?.map((objectItem) => objectItem.id) || []);
  };

  return (
    <Modal
      centered
      styles={{
        body: {
          maxHeight: isBelowMd ? '80vh' : 'none',
          overflowY: isBelowMd ? 'auto' : 'visible',
        },
        content: {
          justifySelf: 'center',
          borderRadius: 24,
          width: '100%',
        },
      }}
      width={window.innerWidth < 744 ? '100%' : '1193px'}
      closeIcon={<X className="w-8 h-8 text-[#B9B9B9]" />}
      open={isModalOpen}
      onCancel={handleCancel}
      title={
        <div className="flex md:flex-row flex-col gap-4 w-full md:w-[746px] md:items-center text-[24px] text-bold">
          <span className="text-gray-10">{t('placingOrder')}</span>
          <Select
            isClearIconHidden
            name="objectAddressId"
            value={selectedObjectAddressId?.toString()}
            onItemSelect={(value) => setSelectedObjectAddressId(Number(value))}
            className="flex-1"
            placeholder={t('objectAddress', { ns: 'serviceHistoryPage' })}
            options={filtersQuery.data?.addresses?.map((address) => ({
              value: address.id.toString(),
              label: address.address,
            }))}
            leftSide={<Map01 className="w-6 h-6 text-brand-brown" />}
          />
        </div>
      }
      footer={false}
    >
      <ConfigProvider locale={localeMap[currentLanguage]?.antd || enUS}>
        <div className="flex divide-y divide-y-[#EFEFEF h-full md:divide-y-0 md:flex-row flex-col flex-1 justify-between gap-4 md:gap-8">
          <div className="flex flex-col w-full md:w-[746px]">
            <Spin
              spinning={
                objectItemDischargesQuery.isFetching || additionalDischargesQuery.isFetching || orderAdditionalDischargeMutation.isPending
              }
            >
              <Calendar
                onSelect={handleDateSelect}
                disabledDate={disabledDate}
                headerRender={() => (
                  <div className="flex h-[52px] justify-between items-center border-b border-b-[#F0F0F0]">
                    <span className="text-[20px] font-semibold">
                      {dayjs(currentMonth)
                        .locale(currentLanguage)
                        .format('MMMM YYYY')
                        .replace(/^\w/, (c) => c.toUpperCase())}
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleMonthChange('prev')}
                        className="bg-[#D5DC2E] h-9 w-9 rounded-[8px] outline-none flex items-center justify-center"
                      >
                        <ChevronLeft className="text-gray-10 stroke-2 w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleMonthChange('next')}
                        className="bg-[#D5DC2E] h-9 w-9 rounded-[8px] outline-none flex items-center justify-center"
                      >
                        <ChevronRight className="text-gray-10 stroke-2 w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
                fullCellRender={renderDate}
                value={dayjs(currentMonth)}
              />
            </Spin>
            <div className="flex sm:flex-row flex-col gap-4 mt-4 justify-between">
              <div className="flex gap-3 items-center">
                <span className="w-[56px] h-[32px] rounded-[8px] bg-[#EBF163]" />
                <span className="text-gray-10 font-medium text-base">{t('scheduledForContainerEmptying')}</span>
              </div>
              <div className="flex gap-3 items-center">
                <span className="w-[56px] h-[32px] rounded-[8px] bg-[#FB923C]" />
                <span className="text-gray-10 px-[3px] py-[3px] font-medium text-base">{t('additionalOrderAvailable')}</span>
              </div>
            </div>
          </div>
          <div className="flex-1 md:pt-0 pt-4 flex flex-col">
            {objectItemsQuery.isPending ? (
              <Skeleton.Node active className="w-full h-full" />
            ) : (
              <>
                <div className="flex justify-between mb-4 items-center">
                  <span className="text-xl font-semibold">{t('container', { ns: 'containersPage' })}</span>
                  <div className="flex gap-3 items-center">
                    <Checkbox
                      checked={selectedObjectItemIds?.length === availableObjectItems?.length}
                      onClick={handleSelectAllObjectItems}
                    />
                    <span className="text-base font-semibold">{t('selectAll', { ns: 'common' })}</span>
                  </div>
                </div>
                <div className="flex gap-3 flex-wrap mb-4">
                  {availableGarbage?.map((garbage) => (
                    <div
                      key={garbage.id}
                      onClick={() => setSelectedGarbageId(garbage.id)}
                      className="flex items-center flex-col gap-2 cursor-pointer"
                    >
                      <img src={`/garbage/${i18n.language}/${garbage.id}.svg`} alt="garbage-icon" className="w-16 object-contain" />
                      <RadioButton selected={selectedGarbageId === garbage.id} />
                    </div>
                  ))}
                </div>
                <div className="max-h-[50vh] md:overflow-y-auto flex flex-col gap-4">
                  {Boolean(availableGarbage?.length) &&
                    availableObjectItems?.map((objectItem) => (
                      <div
                        key={objectItem.id}
                        className={cn(
                          'py-3 px-4 rounded-lg px-4 flex justify-between items-center',
                          selectedObjectItemIds?.includes(objectItem.id) ? 'bg-[#F9FAE0]' : 'bg-[#F5F5F5]',
                        )}
                      >
                        <div onClick={() => handleObjectItemSelect(objectItem.id)} className="flex gap-4 cursor-pointer items-center">
                          <Checkbox checked={selectedObjectItemIds?.includes(objectItem.id)} />
                          <span className="text-gray-10 font-medium">{objectItem.itemType?.name}</span>
                        </div>
                        <span className="text-[#A7AD2A] flex-1 text-right">{objectItem.id}</span>
                      </div>
                    ))}
                </div>
              </>
            )}
          </div>
        </div>
        <Modal
          open={isDetailsModalVisible}
          loading={orderAdditionalDischargeMutation.isPending || cancelDischargeMutation.isPending}
          onCancel={() => setIsDetailsModalVisible(false)}
          styles={{
            content: {
              width: '100%',
              justifySelf: 'center',
              borderRadius: 24,
            },
          }}
          title={
            <div className="flex gap-4 items-center text-[24px] text-bold">
              <span className="text-gray-10">{isDischargeDate ? t('doYouWantToCancel') : t('orderExtraDischarge')}</span>
            </div>
          }
          footer={
            <div className="gap-4 flex">
              <button
                disabled={orderAdditionalDischargeMutation.isPending}
                onClick={() => setIsDetailsModalVisible(false)}
                className="outline-none text-white flex-1 h-12 bg-[#B9B9B9] rounded-[14px] flex justify-center items-center"
              >
                {t('no', { ns: 'common' })}
              </button>
              <button
                disabled={orderAdditionalDischargeMutation.isPending}
                onClick={isDischargeDate ? handleCancelDischarge : handleOrderAdditionalDischarge}
                className={cn(
                  'outline-none text-gray-10 flex-1 h-12 rounded-[14px] flex justify-center items-center',
                  isDischargeDate ? 'bg-[#F97315] text-white' : 'bg-[#D5DC2E]',
                )}
              >
                {isDischargeDate ? t('yes', { ns: 'common' }) : t('order')}
              </button>
            </div>
          }
        >
          <div className="flex gap-2 mb-3 items-center">
            <span>{t('selectedDate')}</span>
            <span className="py-1 px-4 rounded-[8px] bg-[#EBF163] text-gray-10">{dayjs(selectedDate).format(DATE_FORMAT)}</span>
          </div>
          {!isDischargeDate && <span className="text-gray-10">{t('orderDischargeForMultipleContainers')}:</span>}
          {isDischargeDate ? (
            <div className="my-4 flex max-h-[400px] overflow-y-auto flex-col gap-2">
              {objectItemDischargesQuery.data
                ?.filter((discharge) => discharge.date === selectedDate)
                ?.map((discharge) => (
                  <div key={discharge.id} className="h-14 px-4 items-center flex gap-4 justify-between rounded-lg bg-[#F5F5F5]">
                    <img src={`/garbage/${i18n.language}/${discharge.garbage?.id}.svg`} alt="garbage-icon" className="w-6 object-contain" />
                    <span className="flex-1 text-gray-10 font-medium">{discharge.itemType?.name}</span>
                    <span className="text-[#A7AD2A]">{formatAsCurrency(Number(discharge.priceWithVat) || 0)}</span>
                  </div>
                ))}
            </div>
          ) : (
            <div className="my-4 flex max-h-[400px] overflow-y-auto flex-col gap-2">
              {availableObjectItems
                ?.filter((objectItem) => selectedObjectItemIds.includes(objectItem.id))
                .map((objectItem) => (
                  <div key={objectItem.id} className="h-14 px-4 items-center flex gap-4 justify-between rounded-lg bg-[#F5F5F5]">
                    <img
                      src={`/garbage/${i18n.language}/${objectItem.garbage?.id}.svg`}
                      alt="garbage-icon"
                      className="w-6 object-contain"
                    />
                    <span className="flex-1 text-gray-10 font-medium">{objectItem.itemType?.name}</span>
                    <span className="text-[#A7AD2A]">
                      {formatAsCurrency(
                        Number(
                          additionalDischargesQuery.data
                            ?.find((discharge) => discharge.objectItemId === objectItem.id)
                            ?.additionalDischarges?.find((discharge) => discharge.date === selectedDate)?.successPriceWithVat,
                        ) || 0,
                      )}
                    </span>
                  </div>
                ))}
            </div>
          )}
          <span className="block text-right mb-4">
            {t('totalPrice')}: {getPriceByDate(selectedDate)}
          </span>
        </Modal>
      </ConfigProvider>
    </Modal>
  );
};

export default ObjectItemDischargesModal;
